import { Grid } from '@mui/material';
import { HSpacer, RoutalPalette, RoutalToggle, VSpacer } from '@nx-smartmonkey/ui';
import { ReactElement, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import styled from 'styled-components';
import { CapacityIcon } from '../../../../../../assets/svg/Plan/analytics/capacity/CapacityIcon';
import { VolumeIcon } from '../../../../../../assets/svg/Plan/analytics/capacity/VolumeIcon';
import { WeightIcon } from '../../../../../../assets/svg/Plan/analytics/capacity/WeightIcon';
import { useRetrieveActiveProject } from '../../../../../../hooks/project/useRetrieveActiveProject';
import Text from '../../../../../../react-components/Text';
import { useRetrieveAnalyticsPlanCapacities } from '../../../hooks/analytics/useRetrieveAnalyticsPlanCapacities';
import { AnalyticsElement } from './AnalyticsElement';

interface CharDataProps {
  value: number;
  color: string;
  backgroundColor: string;
}

const PlanCapacityContainer = styled(Grid)<{ backgroundColor: string }>`
  display: flex;
  width: 100%;
  max-width: 600px;
  position: relative;
  padding: 12px 20px;
  border-radius: 10px;
  background-color: ${(props) => props.backgroundColor};
  flex: 1;
`;

const PlanCapacityIcon = styled(Grid)`
  display: flex;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 10px;
  align-items: center;
  border-radius: 10px;
`;

interface PlanCapacityElementProps {
  icon: ReactElement;
  backgroundColor: string;
  charData: CharDataProps;
  value: number | string | ReactElement;
  subtitleId: string;
}

const PlanCapacityElement = ({ icon, backgroundColor, charData, value, subtitleId }: PlanCapacityElementProps) => {
  return (
    <PlanCapacityContainer backgroundColor={backgroundColor}>
      <PlanCapacityIcon>{icon}</PlanCapacityIcon>
      <Grid
        width="100%"
        height="100%"
        maxWidth="500px"
        position="relative"
        style={{ display: `flex`, alignItems: `center`, justifyContent: `center` }}
      >
        <Grid
          sx={{
            width: `fit-content`,
            height: `fit-content`,
            borderRadius: `${Number.MAX_VALUE}px`,
            backgroundColor: charData.backgroundColor,
            padding: `10px`,
          }}
        >
          <Doughnut
            data={{
              labels: [`capacity`, `void`],
              datasets: [
                {
                  data: [charData.value, 100 - charData.value],
                  backgroundColor: [charData.color, `transparent`],
                  borderColor: [charData.color, `transparent`],
                },
              ],
            }}
            options={{
              plugins: {
                legend: {
                  display: false,
                },
              },
              cutout: `80%`,
            }}
          />
        </Grid>
        <Grid position="absolute" style={{ inset: 0, display: `flex`, alignItems: `center`, justifyContent: `center` }}>
          {value}
        </Grid>
      </Grid>
    </PlanCapacityContainer>
  );
};

type ValueMode = `total` | `percentage`;

export const PlanCapacity = () => {
  const [valueMode, setValueMode] = useState<ValueMode>(`total`);
  const { data: activeProject } = useRetrieveActiveProject();
  const { totalWeight, totalVolume, capacityWeight, capacityVolume, weightRate, volumeRate } =
    useRetrieveAnalyticsPlanCapacities();

  const volumeActivated = useMemo(() => {
    return activeProject?.constraints.volume;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject]);

  const weightActivated = useMemo(() => {
    return activeProject?.constraints.weight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject]);

  if (!activeProject) {
    return null;
  }

  const charData = {
    volume: {
      value: volumeRate,
      color: RoutalPalette.secondary60,
      backgroundColor: `#EBEDEF`,
    } as CharDataProps,
    weight: {
      value: weightRate,
      color: RoutalPalette.primary60,
      backgroundColor: RoutalPalette.primary40n,
    } as CharDataProps,
  };

  const getValue = (value: string, secondValue: string, color: string) => {
    return (
      <Grid display="flex" flexDirection="column" alignItems="center">
        <Text variant="h5">{value}</Text>
        <VSpacer small />
        <div style={{ height: 0, width: `60px`, borderBottom: `1px solid ${color}` }} />
        <VSpacer small />
        <Text variant="h5">{secondValue}</Text>
      </Grid>
    );
  };

  return (
    <AnalyticsElement
      header={
        <>
          <Grid display="flex" flexDirection="row" alignItems="center">
            <CapacityIcon style={{ width: `18px`, height: `18px` }} />
            <HSpacer small />
            <Text variant="h6" style={{ fontWeight: `600` }} intlId="planner.plan_details.analytics.capacity.title" />
          </Grid>
          <Grid>
            <RoutalToggle
              onChange={(option) => {
                if (option === `left`) {
                  setValueMode(`percentage`);
                }
                if (option === `right`) {
                  setValueMode(`total`);
                }
              }}
              value={valueMode === `percentage` ? `left` : `right`}
              containerStyles={{
                width: `fit-content`,
                boxShadow: `0px 0px 4px 0px rgba(0, 0, 0, 0.25)`,
              }}
              leftChild={
                <Grid width="20px" height="20px" display="flex" alignItems="center" justifyContent="center">
                  <Text
                    style={{
                      color: valueMode === `percentage` ? RoutalPalette.neutral00 : RoutalPalette.secondary60,
                      textAlign: `center`,
                      cursor: valueMode === `percentage` ? `default` : `pointer`,
                    }}
                  >
                    %
                  </Text>
                </Grid>
              }
              rightChild={
                <Grid width="20px" height="20px" display="flex" alignItems="center" justifyContent="center">
                  <Text
                    style={{
                      color: valueMode === `total` ? RoutalPalette.neutral00 : RoutalPalette.secondary60,
                      textAlign: `center`,
                      cursor: valueMode === `percentage` ? `pointer` : `default`,
                    }}
                  >
                    {volumeActivated && !weightActivated ? activeProject.units?.volume : activeProject.units?.weight}
                  </Text>
                </Grid>
              }
            />
          </Grid>
        </>
      }
      headerStyle={{ justifyContent: `space-between` }}
      content={
        <Grid display="flex" flexDirection="row" width="100%" justifyContent="center">
          {weightActivated && (
            <PlanCapacityElement
              icon={<WeightIcon />}
              backgroundColor={RoutalPalette.primary20n}
              charData={charData.weight}
              value={getValue(
                valueMode === `percentage`
                  ? `${charData.weight.value}%`
                  : `${totalWeight} ${activeProject?.units?.weight}`,
                `${capacityWeight} ${activeProject?.units?.weight}`,
                charData.weight.color
              )}
              subtitleId={`test`}
            />
          )}

          {weightActivated && volumeActivated && <HSpacer small />}

          {volumeActivated && (
            <PlanCapacityElement
              icon={<VolumeIcon height="22px" width="21px" />}
              backgroundColor={RoutalPalette.secondary20n}
              charData={charData.volume}
              value={getValue(
                valueMode === `percentage`
                  ? `${charData.volume.value}%`
                  : `${totalVolume} ${activeProject?.units?.volume}`,
                `${capacityVolume} ${activeProject?.units?.volume}`,
                charData.volume.color
              )}
              subtitleId={`test`}
            />
          )}
        </Grid>
      }
    />
  );
};
